import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface CollectionRow {
  id: string;
  workspace_id: string;
  invoice_number: string | null;
  currency: string;
  total_cents: string;
  paid_cents: string;
  due_date: string | null;
  dunning_stage: number;
  dunning_paused: boolean;
  last_dunned_at: string | null;
  customer_email: string | null;
  customer_name: string | null;
  primary_locale: string | null;
  days_overdue: number;
  balance_cents: number;
}

export function useCollections(wsId: string | undefined) {
  return useQuery<CollectionRow[]>({
    queryKey: ['collections', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<CollectionRow[]>(`/workspaces/${wsId}/collections`)).data,
  });
}

export function useRunDunning(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<{ scanned: number; sent: number }>(`/workspaces/${wsId}/collections/run`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collections', wsId] }),
  });
}

export function useRemindCollection(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<{ stage: number }>(`/workspaces/${wsId}/collections/${id}/remind`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collections', wsId] }),
  });
}

export function usePauseCollection(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, paused }: { id: string; paused: boolean }) =>
      (await api.post<{ paused: boolean }>(`/workspaces/${wsId}/collections/${id}/pause`, { paused })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collections', wsId] }),
  });
}
